/**
 * Casamento derivacional entre uma chave de rascunho e um controle observado.
 *
 * A chave vem do contrato publicado pelo backend; o rótulo vem do DOM que o
 * observador já resolveu. Não há aliases locais: uma ausência ou uma
 * ambiguidade é uma recusa explícita, nunca uma escolha por posição.
 */

const MARCAS_DIACRITICAS = /\p{Diacritic}/gu

export interface CampoObservadoParaCasamento {
  /** `name`, ou `id` quando o controle não tem `name`. */
  nome?: string | null
  /** Mantido à parte para que `name` e `id` sejam igualmente autoritativos. */
  id?: string | null
  /** Rótulo que a pessoa vê; pode faltar em um controle mal marcado. */
  rotulo?: string | null
}

export type MotivoDoCasamento = 'campo_nao_encontrado' | 'rotulo_ambiguo'

export interface ResultadoDoCasamento<T> {
  controle: T | null
  motivo: MotivoDoCasamento | null
}

/**
 * Divide separadores e camelCase, tira acentos e descarta apenas o sufixo de
 * identificador. `dataDeAdmissao_id` e `data_de_admissao` passam a ter a
 * mesma sequência de tokens.
 */
export function tokenizarParaCasamento (valor: string | null | undefined): string[] {
  if (typeof valor !== 'string' || valor.trim().length === 0) return []

  const tokens = valor
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .normalize('NFD')
    .replace(MARCAS_DIACRITICAS, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)

  if (tokens.length > 1 && tokens[tokens.length - 1] === 'id') tokens.pop()
  return tokens
}

export function normalizarParaCasamento (valor: string | null | undefined): string {
  return tokenizarParaCasamento(valor).join(' ')
}

function sequenciaEhPrefixo (prefixo: readonly string[], valor: readonly string[]): boolean {
  if (prefixo.length === 0 || valor.length < prefixo.length) return false
  return prefixo.every((token, indice) => token === valor[indice])
}

function atributoCasa (chave: readonly string[], valor: string | null | undefined): boolean {
  const tokens = tokenizarParaCasamento(valor)
  return tokens.length > 0 && tokens.length === chave.length && tokens.every((token, indice) => token === chave[indice])
}

/**
 * Casa uma chave com exatamente um controle observado.
 *
 * Atributo `name`/`id` exige igualdade normalizada. Rótulo usa prefixo de
 * tokens, de modo que `nome` casa com `Nome completo`, mas não com
 * `Nomeado`. Mais de um candidato é sempre ambiguidade.
 */
export function casarCampoComControle<T extends CampoObservadoParaCasamento> (
  chaveDoCampo: string,
  camposObservados: readonly T[]
): ResultadoDoCasamento<T> {
  const chave = tokenizarParaCasamento(chaveDoCampo)
  if (chave.length === 0) return { controle: null, motivo: 'campo_nao_encontrado' }

  const candidatos = camposObservados.filter(campo => {
    const casaPorAtributo = atributoCasa(chave, campo.nome) || atributoCasa(chave, campo.id)
    const casaPorRotulo = sequenciaEhPrefixo(chave, tokenizarParaCasamento(campo.rotulo))
    return casaPorAtributo || casaPorRotulo
  })

  if (candidatos.length === 0) return { controle: null, motivo: 'campo_nao_encontrado' }
  if (candidatos.length > 1) return { controle: null, motivo: 'rotulo_ambiguo' }
  return { controle: candidatos[0], motivo: null }
}
